import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
} from "@-/com.npmjs.tanstack__react-query";
import arrayLength from "@-/com.npmjs.extra-array__length";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  morphemeDetailsTileSliceQueryFn,
  useGetMorphemeDetailsTileSliceQueryKey,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useEffect, useMemo } from "react";

/**
 * Fronts per details page. ~10 rows of the 4-column phone grid — more than a viewport, so one
 * page fills the screen and the next is already being fetched by the time it scrolls into view.
 * Comfortably under the 64-lookup DataLoader batch the slice read is bounded to.
 */
export const DECK_SECTION_PAGE_SIZE = 40;

type DeckCardDetailsPage = {
  offset: number;
  slices: (MorphemeDetailsTileSlice | null)[];
};

const EMPTY_DETAILS: ReadonlyMap<string, MorphemeDetailsTileSlice | null> = new Map();

/**
 * The content/static-DB details for an ordered list of deck fronts, fetched page by page as an
 * infinite query that follows the section's revealed window: it keeps fetching the next page
 * while fewer than `requiredCount` fronts have details, one page at a time.
 *
 * Each page resolves its fronts through the per-front `morphemeDetailsTileSlice` query (so it
 * shares that cache with the morpheme panels and coalesces into one batched DB read via the
 * shared DataLoader) and is keyed by the ordered front list, so a sort change starts a fresh
 * sequence — served from the per-front cache for anything already seen.
 *
 * `detailsByFront` only holds fronts whose page has resolved (a `null` value means the catalog
 * has no card for that front); a front absent from the map is still loading. Nothing here
 * blocks rendering — the section paints from actor-only core slices and swaps these in.
 */
export function useDeckCardDetailsInfiniteQuery({
  enabled = true,
  fronts,
  requiredCount,
}: {
  enabled?: boolean;
  fronts: readonly string[];
  requiredCount: number;
}) {
  const getMorphemeDetailsTileSliceQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  const frontCount = arrayLength(fronts);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery(
    infiniteQueryOptions({
      enabled: enabled && frontCount > 0,
      queryFn: async ({ client, pageParam }): Promise<DeckCardDetailsPage> => {
        const pageFronts = fronts.slice(pageParam, pageParam + DECK_SECTION_PAGE_SIZE);
        const slices = await Promise.all(
          pageFronts.map((front) =>
            client.fetchQuery(
              queryOptions({
                queryFn: morphemeDetailsTileSliceQueryFn,
                queryKey: getMorphemeDetailsTileSliceQueryKey({ morpheme: front }),
              }),
            ),
          ),
        );
        return { offset: pageParam, slices };
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage: DeckCardDetailsPage): number | undefined => {
        const nextOffset = lastPage.offset + DECK_SECTION_PAGE_SIZE;
        return nextOffset >= frontCount ? undefined : nextOffset;
      },
      // The key getter is a per-render closure over the DB-scoped loader; it hashes to `{}` so a
      // new identity never invalidates the sequence, but listing it keeps the dependency explicit.
      queryKey: ["deckCardDetails", { getMorphemeDetailsTileSliceQueryKey }, ...fronts] as const,
    }),
  );

  const loadedCount = useMemo(
    () => data?.pages.reduce((count, page) => count + arrayLength(page.slices), 0) ?? 0,
    [data],
  );

  // Follow the revealed window: one page at a time, never past a failed page (a retry loop on
  // an offline catalog would otherwise hammer the network while the tiles already paint from
  // their core slices).
  useEffect(() => {
    if (
      enabled &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isError &&
      !isFetchNextPageError &&
      loadedCount < requiredCount
    ) {
      void fetchNextPage();
    }
  }, [
    enabled,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    loadedCount,
    requiredCount,
  ]);

  const detailsByFront = useMemo(() => {
    if (data === undefined) {
      return EMPTY_DETAILS;
    }
    const map = new Map<string, MorphemeDetailsTileSlice | null>();
    for (const page of data.pages) {
      for (const [index, slice] of page.slices.entries()) {
        const front = fronts[page.offset + index];
        if (front !== undefined) {
          map.set(front, slice);
        }
      }
    }
    return map;
  }, [data, fronts]);

  return { detailsByFront, isFetching };
}
